import React, { useState, useEffect } from 'react'
import { Box, Button } from '@material-ui/core'
import {
  FieldNames,
  EventLogKeys,
  TimelineDotColors,
  EventDetailRow1,
  EventDetailRow2,
  EventDetailRow3,
  EventDetailRow4,
  IntervalHourlySummaryHeadings,
  IntervalDetailsHeadings,
  EVENTS_EMPTY_TABLE_TEXT,
} from './constants'
import { useStyles } from './styles'
import TableData from './TableData'
import IntervalGraph from './IntervalGraph'

const EventsTable = (props) => {
  const {
    tableHeadings,
    tableContents,
    sortBy,
    onExpandedRowsChange,
    paginate = false,
    page = 0,
    rowsPerPage = 25,
    onPageChange,
    onRowsPerPageChange,
  } = { ...props }
  const classes = useStyles()
  const [isBold, setIsBold] = useState('')
  const [expandedRows, setExpandedRows] = useState({})
  const [expandedIntervals, setExpandedIntervals] = useState({})

  useEffect(() => {
    setIsBold(sortBy)
  }, [sortBy])

  const toggleRow = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const toggleIntervalRow = (index) => {
    setExpandedIntervals(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const closeAllExpandedRows = () => {
    setExpandedRows({})
    setExpandedIntervals({})
  }

  useEffect(() => {
    if (onExpandedRowsChange) {
      onExpandedRowsChange({ closeAllExpandedRows })
    }
  }, [onExpandedRowsChange, closeAllExpandedRows])

  const getStatusBadge = (status) => {
    const badgeMap = {
      COMMANDCOMPLETED: 'badgeGreen',
      STORMACTIVE: 'badgeTeal',
      PUBLISHED: 'badgeBlue',
      COMMANDSENT: 'badgeYellow',
      REQUESTED: 'badgePurple',
      FAILED: 'badgeRed',
    }
    const badgeClass = badgeMap[status] || 'badgeGray'
    return { text: status, className: badgeClass }
  }

  const getSubtypeBadge = (subtype) => {
    if (!subtype) return { text: '—', className: 'badgeGray' }
    return { text: subtype.replace(/_/g, ' '), className: 'badgeBlue' }
  }

  const renderMultiValueBadges = (values, badgeClassName = 'badgeGray') => {
    return (
      <>
        {values.map((value, index) => (
          <React.Fragment key={index}>
            {index > 0 && ' / '}
            <Box component="span" className={`${classes.badge} ${classes[badgeClassName]}`}>
              {value ?? 0}
            </Box>
          </React.Fragment>
        ))}
      </>
    )
  }

  const buildEventLogs = (eventData) => {
    const eventLogs = EventLogKeys
      .map(eventLogKey => ({
        timestamp: eventData[eventLogKey + 'EventLogTimestamp'],
        eventType: eventData[eventLogKey + 'EventLogType']
      }))
      .filter(log => log.timestamp)

    return (
      <Box className={classes.timeline}>
        {eventLogs.map((log, logIndex) => (
          <Box key={logIndex} className={classes.tlItem}>
            <Box
              className={classes.tlDot}
              style={{
                background: TimelineDotColors[logIndex],
                boxShadow: `0 0 0 1px ${TimelineDotColors[logIndex]}1a`
              }}
            />
            <Box className={classes.tlTime}>{log.timestamp}</Box>
            <Box className={classes.tlText}>{log.eventType}</Box>
          </Box>
        ))}
      </Box>
    )
  }

  const buildEventDetails = (eventData) => {
    const KeyValueItem = ({ label, value }) => (
      <Box className={classes.kvItem}>
        <Box className={classes.kvLabel}>{label}</Box>
        <Box className={classes.kvValue}>{value ?? '—'}</Box>
      </Box>
    )

    const getFieldValue = (eventData, field) => {
      if (field === FieldNames.P1P2P3) {
        return `${eventData.p1 ?? '—'} / ${eventData.p2 ?? '—'} / ${eventData.p3 ?? '—'}`
      }
      if (field === FieldNames.R1R2R3) {
        return `${eventData.r1 ?? '—'} / ${eventData.r2 ?? '—'} / ${eventData.r3 ?? '—'}`
      }
      return eventData[field]
    }

    const detailRows = [EventDetailRow1, EventDetailRow2, EventDetailRow3, EventDetailRow4]
    const hasFailureReason = eventData.status === 'FAILED' && eventData.failureReason

    return (
      <>
        {detailRows.map((row, rowIndex) => (
          <Box
            key={rowIndex}
            className={rowIndex > 0 ? `${classes.kvGridSpaced} ${classes.kvGrid}` : classes.kvGrid}
          >
            {row
              .filter(item => item.field !== FieldNames.FAILURE_REASON || hasFailureReason)
              .map((item, i) => (
                <KeyValueItem key={i} label={item.label} value={getFieldValue(eventData, item.field)} />
              ))}
          </Box>
        ))}
      </>
    )
  }

  const exportIntervalsCsv = (intervalData) => {
    const intervals = intervalData.intervals
    const eventName = intervalData.name || 'event'

    if (!intervals || intervals.length === 0) return

    const headers = [
      'Sr No', 'Reading Timestamp', 'End At (Epoch)',
      'Charge (Wh)', 'Charge Devices Reporting',
      'Discharge (Wh)', 'Discharge Devices Reporting',
      'SOC (%)', 'SOC Devices Reporting',
      'Charge Power (W)', 'Discharge Power (W)'
    ]

    // Escape CSV values to handle commas, quotes, and newlines
    const escapeCsvValue = (value) => {
      const stringValue = value == null ? '' : String(value)
      return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')
        ? `"${stringValue.replace(/"/g, '""')}"`
        : stringValue
    }

    const rows = intervals.map((interval, index) => [
      index + 1,
      interval.readingTimestamp,
      interval.end_at,
      interval.charge?.enwh || 0,
      interval.charge?.devices_reporting || 0,
      interval.discharge?.enwh || 0,
      interval.discharge?.devices_reporting || 0,
      interval.soc?.percent ? interval.soc.percent.toFixed(2) : '0.00',
      interval.soc?.devices_reporting || 0,
      interval.chargePower ? interval.chargePower.toFixed(2) : '0.00',
      interval.dischargePower ? interval.dischargePower.toFixed(2) : '0.00'
    ].map(escapeCsvValue).join(','))

    const csvContent = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const downloadUrl = URL.createObjectURL(blob)
    const downloadLink = document.createElement('a')
    downloadLink.href = downloadUrl
    downloadLink.download = `intervals-${eventName.replace(/\s+/g, '-')}.csv`
    downloadLink.click()
    URL.revokeObjectURL(downloadUrl)
  }

  const buildIntervals = (intervalData) => {
    const intervals = intervalData.intervals

    if (!intervals || intervals.length === 0) {
      return <Box className={classes.noIntervalData}>No interval data available.</Box>
    }

    const totalCharge = intervals.reduce((accumulator, interval) => accumulator + (interval.charge?.enwh || 0), 0)
    const totalDischarge = intervals.reduce((accumulator, interval) => accumulator + (interval.discharge?.enwh || 0), 0)
    const devicesReporting = intervals[0]?.charge?.devices_reporting ?? '—'

    const hourMap = {}
    intervals.forEach(interval => {
      if (!interval.readingTimestamp) return

      const [datePart, timePart] = interval.readingTimestamp.split(' ')
      const [hours, minutes] = timePart.split(':').map(Number)
      let hourKey

      if (minutes === 0) {
        if (hours === 0) {
          const [year, month, day] = datePart.split('-').map(Number)
          const previousDate = new Date(year, month - 1, day - 1)
          const previousDateString = `${previousDate.getFullYear()}-${String(previousDate.getMonth() + 1).padStart(2, '0')}-${String(previousDate.getDate()).padStart(2, '0')}`
          hourKey = `${previousDateString} 23:00`
        } else {
          hourKey = `${datePart} ${String(hours - 1).padStart(2, '0')}:00`
        }
      } else {
        hourKey = `${datePart} ${String(hours).padStart(2, '0')}:00`
      }

      if (!hourMap[hourKey]) hourMap[hourKey] = { charge: 0, discharge: 0 }
      hourMap[hourKey].charge += interval.charge?.enwh || 0
      hourMap[hourKey].discharge += interval.discharge?.enwh || 0
    })

    const hourKeys = Object.keys(hourMap).sort()

    const hourlySummaryData = hourKeys.map((hourKey) => ({
      hour: hourKey,
      charge: hourMap[hourKey].charge,
      discharge: hourMap[hourKey].discharge,
      net: hourMap[hourKey].discharge - hourMap[hourKey].charge,
    }))

    const intervalDataTransformed = intervals.map((interval, index) => ({
      index: index + 1,
      readingTime: interval.readingTimestamp ?? '—',
      endAt: interval.end_at ?? '—',
      charge: interval.charge?.enwh || 0,
      discharge: interval.discharge?.enwh || 0,
      soc: interval.soc?.percent ? interval.soc.percent.toFixed(1) : '0.0',
      chargePower: interval.chargePower ? interval.chargePower.toFixed(1) : '0.0',
      dischargePower: interval.dischargePower ? interval.dischargePower.toFixed(1) : '0.0',
    }))

    return (
      <>
        <IntervalGraph intervals={intervals} />

        <Box className={classes.summaryRow}>
          <Box className={`${classes.statChip} ${classes.statChipBlue}`}>
            <Box className={classes.statChipVal}>{intervals.length}</Box>
            <Box className={classes.statChipLbl}>Total Intervals</Box>
          </Box>
          <Box className={`${classes.statChip} ${classes.statChipGreen}`}>
            <Box className={classes.statChipVal}>{totalCharge} Wh</Box>
            <Box className={classes.statChipLbl}>Total Charge</Box>
          </Box>
          <Box className={`${classes.statChip} ${classes.statChipOrange}`}>
            <Box className={classes.statChipVal}>{totalDischarge} Wh</Box>
            <Box className={classes.statChipLbl}>Total Discharge</Box>
          </Box>
          <Box className={`${classes.statChip} ${classes.statChipPurple}`}>
            <Box className={classes.statChipVal}>{devicesReporting}</Box>
            <Box className={classes.statChipLbl}>Devices Reporting</Box>
          </Box>
        </Box>

        {hourKeys.length > 1 && (
          <Box marginBottom="16px">
            <Box className={classes.sectionTitle} marginBottom="8px">Hourly Summary</Box>
            <TableData
              classes={classes}
              tableHeadings={IntervalHourlySummaryHeadings}
              tableContents={hourlySummaryData}
              renderCellContent={(item, heading, index) => {
                switch (heading.id) {
                  case 'index':
                    return <Box component="span">{index + 1}</Box>
                  case 'hour':
                    return <Box component="span" className={classes.chip}>{item.hour}</Box>
                  case 'charge':
                    return (
                      <Box component="span" className={`${classes.chip} ${classes.chipGreen}`}>
                        {item.charge} Wh
                      </Box>
                    )
                  case 'discharge':
                    return (
                      <Box component="span" className={`${classes.chip} ${classes.chipOrange}`}>
                        {item.discharge} Wh
                      </Box>
                    )
                  case 'net':
                    return (
                      <Box component="span" className={`${classes.chip} ${item.net > 0 ? classes.chipOrange : item.net < 0 ? classes.chipGreen : ''}`}>
                        {item.net} Wh
                      </Box>
                    )
                  default:
                    return item[heading.id]
                }
              }}
              containerClassName={classes.intervalTableWrap}
              tableClassName={classes.intervalTableCompact}
              stickyHeader={false}
            />
          </Box>
        )}

        <TableData
          classes={classes}
          tableHeadings={IntervalDetailsHeadings}
          tableContents={intervalDataTransformed}
          renderCellContent={(item, heading) => {
            switch (heading.id) {
              case 'readingTime':
              case 'endAt':
                return <Box component="span" className={classes.chip}>{item[heading.id]}</Box>
              case 'charge':
              case 'discharge':
                return (
                  <Box component="span" className={`${classes.chip} ${item[heading.id] > 0 ? (heading.id === 'charge' ? classes.chipGreen : classes.chipOrange) : ''}`}>
                    {item[heading.id]} Wh
                  </Box>
                )
              case 'soc':
                return (
                  <Box component="span" className={`${classes.chip} ${classes.chipBlue}`}>
                    {item.soc}%
                  </Box>
                )
              case 'chargePower':
              case 'dischargePower':
                return `${item[heading.id]} W`
              default:
                return item[heading.id]
            }
          }}
          containerClassName={classes.intervalTableWrap}
          tableClassName={classes.intervalTableCompact}
          stickyHeader={false}
        />
      </>
    )
  }

  const renderCellContent = (item, heading, index) => {
    const value = item[heading.id]
    const pageOffset = paginate ? (page * rowsPerPage) : 0

    switch (heading.id) {
      case 'index':
        return pageOffset + index + 1

      case 'status':
        const statusBadge = getStatusBadge(value)
        return (
          <Box component="span" className={`${classes.badge} ${classes[statusBadge.className]}`}>
            {statusBadge.text}
          </Box>
        )

      case 'subtype':
        const subtypeBadge = getSubtypeBadge(value)
        return (
          <Box component="span" className={`${classes.badge} ${classes[subtypeBadge.className]}`}>
            {subtypeBadge.text}
          </Box>
        )

      case 'targetSoc':
        return value != null ? (
          <Box component="span" className={`${classes.badge} ${classes.badgeBlue}`}>
            {value}%
          </Box>
        ) : '—'

      case 'rateWatt':
        return value != null ? (
          <Box component="span" className={`${classes.badge} ${classes.badgeOrange}`}>
            {value} W
          </Box>
        ) : '—'

      case 'avgChargePower':
      case 'avgDischargePower':
        return value != null ? `${value} W` : '—'

      case 'avgDischarged':
        return value != null ? `${value} W` : '—'

      case 'startSoc':
      case 'endSoc':
        return value != null ? `${value}%` : '—'

      case FieldNames.P1P2P3:
        return renderMultiValueBadges([item.p1, item.p2, item.p3])

      case FieldNames.R1R2R3:
        return renderMultiValueBadges([item.r1, item.r2, item.r3])

      case 'eventLogs':
        const isOpen = expandedRows[index]
        return (
          <Button
            className={`${classes.logBtn} ${isOpen ? classes.logBtnOpen : ''}`}
            onClick={() => toggleRow(index)}
            disableRipple
          >
            Event Logs <Box component="span" className={classes.arrow}>▾</Box>
          </Button>
        )

      case 'intervals':
        const isIntervalOpen = expandedIntervals[index]
        const intervalCount = item.intervals?.length || 0
        return (
          <Button
            className={`${classes.intervalBtn} ${isIntervalOpen ? classes.intervalBtnOpen : ''}`}
            onClick={() => toggleIntervalRow(index)}
            disableRipple
          >
            View Intervals ({intervalCount}) <Box component="span" className={classes.arrow}>▾</Box>
          </Button>
        )

      default:
        return value ?? '—'
    }
  }

  return (
    <TableData
      classes={classes}
      tableHeadings={tableHeadings}
      tableContents={tableContents}
      isBold={isBold}
      handleSortChange={props.handleSortChange}
      renderCellContent={renderCellContent}
      expandedRows={expandedRows}
      expandedIntervals={expandedIntervals}
      buildEventLogs={buildEventLogs}
      buildEventDetails={buildEventDetails}
      buildIntervals={buildIntervals}
      exportIntervalsCsv={exportIntervalsCsv}
      emptyTableText={EVENTS_EMPTY_TABLE_TEXT}
      paginate={paginate}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  )
}

export default EventsTable
